/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-19 10:57:36
 **/

import JsonUtils from "./JsonUtils";

type VoidFunction = () => void;

class UtilityStatic {
	/**
	 * Validates the given value based on its type and content.
	 *
	 * @param {unknown} value - The value to be validated.
	 * @return {boolean} Returns true if the value is valid; otherwise, false.
	 */
	public is_validate(value: unknown): boolean {
		if (value) {
			if (typeof value === "string") {
				return value.length > 0;
			}
			return true;
		}
		return false;
	}

	/**
	 * Checks if the provided string matches the given regular expression pattern.
	 *
	 * @param {string} srcData - The input string to be tested against the regular expression.
	 * @param {RegExp} regExp - The regular expression pattern used for testing.
	 * @return {boolean} - Returns true if the string matches the pattern, otherwise false.
	 */
	public is_match(srcData: string, regExp: RegExp): boolean {
		return regExp.test(srcData);
	}

	public noop<T = void>(promise: Promise<T>, thenable: VoidFunction = () => {}, exception: VoidFunction = () => {}) {
		promise.then(thenable).catch(exception);
	}

	/**
	 * Normalizes a given URL by ensuring it includes the appropriate protocol (HTTP or HTTPS).
	 * If the URL is already valid, it is returned as is. If the URL is missing a protocol,
	 * it attempts to prepend 'https:' up to 3 times until the URL is valid.
	 *
	 * @param {string|null} [url] - The URL to normalize. It can be null or undefined.
	 * @return {string} The normalized URL, or an empty string if the input is null or undefined.
	 */
	public url_normalize(url?: string | null): string {
		if (url) {
			let count = 3;
			while (count > 0) {
				try {
					new URL(url); // 주어진 URL확인
					break;
				} catch {
					if (!url.startsWith("http")) {
						url = `http${url.includes("localhost") ? "" : "s"}:${url}`;
					}
				}
				count--;
			}
			return url;
		} else {
			return "";
		}
	}

	/**
	 * Concatenates an array of Uint8Array objects into a single Uint8Array.
	 *
	 * @param {Uint8Array[]} uint8Arrays - An array of Uint8Array objects to concatenate.
	 * @return {Promise<Uint8Array>} A promise that resolves to a single Uint8Array containing all the concatenated data.
	 */
	public async concat_uint8_array(uint8Arrays: Uint8Array[]): Promise<Uint8Array> {
		const blob = new Blob(uint8Arrays);
		const buffer = await blob.arrayBuffer();
		return new Uint8Array(buffer);
	}

	/**
	 * Compresses the given string using the gzip compression algorithm.
	 *
	 * @param {string} str - The string to be compressed.
	 * @return {Promise<Uint8Array>} - A promise that resolves to the compressed data as a Uint8Array.
	 */
	public async compress(str: string): Promise<Uint8Array> {
		const stream = new Blob([str]).stream();
		const compressedStream = stream.pipeThrough(new CompressionStream("gzip"));
		const chunks = [];
		const reader = compressedStream.getReader();
		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			chunks.push(value);
		}
		return await this.concat_uint8_array(chunks);
	}

	/**
	 * Decompresses the given compressed byte array using gzip decompression.
	 *
	 * @param {Uint8Array} compressedBytes - The compressed data as a Uint8Array.
	 * @return {Promise<string>} A promise that resolves to the decompressed string.
	 */
	public async decompress(compressedBytes: Uint8Array): Promise<string | null> {
		const stream = new Blob([compressedBytes]).stream();
		const decompressedStream = stream.pipeThrough(new DecompressionStream("gzip"));

		const chunks = [];
		const reader = decompressedStream.getReader();
		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			chunks.push(value);
		}
		const stringBytes = await this.concat_uint8_array(chunks);
		return JsonUtils.to_string(new TextDecoder().decode(stringBytes));
	}

	public check_append(condition: boolean | (() => boolean), key: string, value: any | (() => any)) {
		if (typeof condition === "function") {
			condition = condition();
		}
		return condition ? { [key]: typeof value === "function" ? value() : value } : {};
	}

	public async sleep(ms: number) {
		return new Promise<void>((resolve) => setTimeout(resolve, ms));
	}
}

const Utility = Object.freeze(new UtilityStatic());
export default Utility;
